import * as tslib_1 from "tslib";
import { Router } from '@angular/router';
import { Observable, of, forkJoin, zip } from 'rxjs';
import { Store } from '@ngrx/store';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { switchMap, catchError, tap, withLatestFrom, map, take, concatMap, filter } from 'rxjs/operators';
import { Selectors, Actions as RootActions } from '../root-reducer';
import { SearchActionTypes, GetScreeningSuccess, GetScreeningFailed, GetSpecificScreeningFailed, PatchSearchResultFailed, PatchSearchResultSuccess, GetSearchValidity, GetSearchValiditySuccess, GetSearchValidityFailure, PostScreeningSuccess, GetSpecificScreeningSuccess, PostScreeningFailed } from 'store/actions/search.actions';
import { SetScreeningError, RemoveScreeningError } from 'store/actions/error.actions';
import { CreateRiskReport, UpdateReport } from 'store/actions/risk-assessment.actions';
import { RecordType } from 'modules/shared/models/record.models';
import { SearchEngineTypeValue } from 'modules/shared/models/screening.models';
import { SearchPushEvent } from 'modules/shared/models/pusher.models';
import { CustomerService } from 'modules/core/services/customer.service';
import { ScreeningService } from 'modules/core/services/screening.service';
import { SnackbarService } from 'app/services/snackbar.service';
import { RecordService } from 'modules/core/services/record.service';
import { ScreeningErrorMessageComponent } from 'modules/shared/components/screening-error-message/screening-error-message.component';
import { GetCustomerStatus } from 'store/actions/customer.actions';
var PUSHER_IGNORE_SEARCHES = [SearchEngineTypeValue.INTERNET_SEARCH, SearchEngineTypeValue.OWN_NAME, SearchEngineTypeValue.NLP];
var SearchEffects = /** @class */ (function () {
    function SearchEffects(actions$, snackBar, screeningService, store, customerService, recordService, router) {
        var _this = this;
        this.actions$ = actions$;
        this.snackBar = snackBar;
        this.screeningService = screeningService;
        this.store = store;
        this.customerService = customerService;
        this.recordService = recordService;
        this.router = router;
        this.GetScreening$ = this.actions$.pipe(ofType(SearchActionTypes.GET_SCREENING), map(function (action) {
            return action.payload;
        }), switchMap(function (payload) {
            var requests$ = [];
            if (payload.engineTypes) {
                payload.engineTypes.forEach(function (engine) {
                    requests$.push(_this.screeningService.getScreening(payload.recordId, engine, payload.parrams));
                });
            }
            else {
                return _this.store
                    .select(Selectors.billable.getSearchEngines)
                    .pipe(filter(function (searchEngines) { return !!searchEngines && !!searchEngines.length; }), take(1), map(function (searchEngines) {
                    return searchEngines.filter(function (searchEngine) { return searchEngine.display; })
                        .map(function (searchEngine) { return searchEngine.serviceType; });
                }), concatMap(function (engines) {
                    engines.forEach(function (engine) {
                        requests$.push(_this.screeningService.getScreening(payload.recordId, engine, payload.parrams));
                    });
                    return forkJoin(requests$);
                }));
            }
            return forkJoin(requests$);
        }), switchMap(function (res) {
            var screening = {};
            if (res && res.length > 0) {
                res.forEach(function (search) {
                    screening[search.engineType] = search;
                });
                screening.isScreening = true;
            }
            return of(new GetScreeningSuccess(screening));
        }), catchError(function (err) { return of(new GetScreeningFailed(err)); }));
        this.PostScreening$ = this.actions$.pipe(ofType(SearchActionTypes.POST_SCREENING), withLatestFrom(this.store.select(Selectors.customer.getCustomer)), tap(function (_a) {
            var actionData = _a[0], customerData = _a[1];
            var customerId;
            if (actionData.payload.customer) {
                customerId = actionData.payload.customer.id;
            }
            else if (customerData) {
                customerId = customerData.id;
            }
            if (actionData.payload.redirectToScreening) {
                _this.router.navigateByUrl("customers/" + customerId + "/screening");
            }
        }), 
        // get customer and crps
        switchMap(function (_a) {
            var actionData = _a[0], customerData = _a[1];
            return of([actionData, customerData]).pipe(switchMap(function (_a) {
                var action = _a[0], customer = _a[1];
                customer = customer || action.payload.customer;
                if (!customer) {
                    throw new Error('Customer not found');
                }
                var crps$;
                if (action.payload.crpId) {
                    crps$ = _this.customerService.getCrp(action.payload.crpId)
                        .pipe(map(function (response) {
                        if (response.status === 'CURRENT') {
                            return [response];
                        }
                        else {
                            return [];
                        }
                    }));
                }
                else {
                    crps$ = _this.customerService.getCustomerCrps({ customerId: customer.id, limit: 999 })
                        .pipe(map(function (response) {
                        return response.results.filter(function (crp) { return crp.status === 'CURRENT'; });
                    }));
                }
                return zip(of(action.payload), of(customer), crps$);
            }), 
            // get search validity
            switchMap(function (_a) {
                var payload = _a[0], customer = _a[1], crps = _a[2];
                // payload.generateReport = false;
                return _this.store
                    .select(Selectors.billable.getSearchEngines)
                    .pipe(filter(function (searchEngines) { return !!searchEngines && !!searchEngines.length; }), take(1), map(function (searchEngines) { return searchEngines.filter(function (searchEngine) {
                    // own restricted list is not added to billable yet, so remaing value is undefined
                    return searchEngine.display && (searchEngine.remaining === undefined || searchEngine.remaining > 0);
                }); }), concatMap(function (engines) {
                    var validity$ = zip.apply(void 0, crps.map(function (crp) {
                        if (crp.recordType === RecordType.CORPORATE) {
                            return _this.recordService.getCorporateRecord(crp.record).pipe(map(function (record) {
                                if (record.isIncorporated) {
                                    return crp;
                                }
                                return null;
                            }));
                        }
                        else {
                            return of(crp);
                        }
                    })).pipe(map(function (validCrps) {
                        return validCrps.filter(function (crp) { return crp !== null; });
                    }), switchMap(function (filteredCrps) {
                        if (filteredCrps.length > 0) {
                            return _this.screeningService.checkValidSearch(filteredCrps.map(function (crp) { return crp.record; }), engines)
                                .pipe(map(function (validities) {
                                validities.map(function (validity) {
                                    validity.name = crps.find(function (crp) { return validity.record === crp.record; }).name;
                                });
                                return validities;
                            }));
                        }
                        else {
                            return of([]);
                        }
                    }));
                    return zip(of(payload), of(customer), of(crps), validity$);
                }));
            }), switchMap(function (_a) {
                var payload = _a[0], customer = _a[1], crps = _a[2], searchToBeDone = _a[3];
                return zip(of(payload), of(customer), of(crps), of(searchToBeDone), _this.store.select(Selectors.customer.getCurrentCrp));
            }), 
            // get whether NLP is enabled
            switchMap(function (_a) {
                var payload = _a[0], customer = _a[1], crps = _a[2], searchToBeDone = _a[3], curentCrp = _a[4];
                return _this.store
                    .select(Selectors.billable.getNLP)
                    .pipe(take(1), concatMap(function (nlpEnabled) {
                    var isNlpEnabled = nlpEnabled ? nlpEnabled.display : false;
                    return zip(of(payload), of(customer), of(crps), of(searchToBeDone), of(curentCrp), of(isNlpEnabled));
                }));
            }), 
            // post screenings based on payload options
            switchMap(function (_a) {
                var payload = _a[0], customer = _a[1], crps = _a[2], searchToBeDone = _a[3], curentCrp = _a[4], isNlpEnabled = _a[5];
                var payload$ = of(payload);
                var posts$ = [];
                var beingGenerated = false;
                if (!searchToBeDone) {
                    searchToBeDone = [];
                }
                if (payload.invalidOnly) {
                    searchToBeDone = searchToBeDone.filter(function (search) { return !search.valid; });
                    if (searchToBeDone.length === 0) {
                        if (!beingGenerated && payload.generateReport) {
                            beingGenerated = true;
                            _this.store.dispatch(new RootActions.riskAssessment.CreateRiskReport({
                                customerId: customer.id,
                                customerName: customer.name,
                                redirectToScreening: payload.redirectToScreening
                            }));
                            _this.snackBar.success('All searches are valid, no screening refreshed, the new risk report is being generated');
                        }
                        else {
                            _this.snackBar.success('All searches are valid, no screening refreshed');
                        }
                    }
                }
                searchToBeDone
                    .filter(function (crpSearch) {
                    if (payload.engineTypes) {
                        return payload.engineTypes.indexOf(crpSearch.engine) >= 0;
                    }
                    else {
                        return crpSearch;
                    }
                })
                    .forEach(function (crpSearch) {
                    if (PUSHER_IGNORE_SEARCHES.indexOf(crpSearch.engine) < 0) {
                        _this.store.dispatch(new RootActions.pusher.SetScreening({
                            customer: {
                                id: customer.id,
                                name: customer.name,
                                screeningRequest: {
                                    redirectToScreening: payload.redirectToScreening,
                                    customerId: customer.id,
                                    crpId: payload.crpId,
                                    engineTypes: payload.engineTypes,
                                    primaryCrp: curentCrp ? curentCrp.primary : false,
                                    recordId: crpSearch.record,
                                    generateReport: payload.generateReport ? SearchPushEvent.GENERATE_REPORT : SearchPushEvent.NO_GENERATE_REPORT
                                }
                            },
                            search: {
                                isRemoved: false,
                                customerId: customer.id,
                                engineType: crpSearch.engine,
                                id: crpSearch.record,
                                crpId: payload.crpId,
                                generateReport: payload.generateReport ? SearchPushEvent.GENERATE_REPORT : SearchPushEvent.NO_GENERATE_REPORT
                            }
                        }));
                    }
                    else if (crpSearch.engine === SearchEngineTypeValue.INTERNET_SEARCH && isNlpEnabled) {
                        _this.store.dispatch(new RootActions.pusher.SetNlp({
                            nlp: {
                                customerId: customer.id,
                                recordId: crpSearch.record
                            }
                        }));
                    }
                    posts$.push(_this.screeningService.postScreening({
                        redirectToScreening: payload.redirectToScreening,
                        customerId: customer.id,
                        crpId: payload.crpId,
                        engineTypes: payload.engineTypes,
                        primaryCrp: curentCrp ? curentCrp.primary : false,
                        recordId: crpSearch.record,
                        generateReport: payload.generateReport ? SearchPushEvent.GENERATE_REPORT : SearchPushEvent.NO_GENERATE_REPORT
                    }, crpSearch.engine)
                        .pipe(map(function (res) {
                        return tslib_1.__assign({}, res, { engineType: crpSearch.engine, record: crpSearch.record });
                    }), catchError(function (error) {
                        _this.store.dispatch(new RootActions.pusher.RemoveScreening({
                            customerId: customer.id,
                            engineType: crpSearch.engine,
                            generateReport: payload.generateReport ? SearchPushEvent.GENERATE_REPORT : SearchPushEvent.NO_GENERATE_REPORT,
                            id: crpSearch.record,
                            error: true
                        }));
                        return of({
                            error: "Fail to screening with " + crpSearch.engine + " for record " + crpSearch.record,
                            name: crpSearch.name,
                            record: crpSearch.record,
                            engineType: crpSearch.engine
                        });
                    })));
                });
                if (searchToBeDone.length === 0 && !payload.generateReport) {
                    if (!beingGenerated && payload.generateReport) {
                        _this.store.dispatch(new RootActions.riskAssessment.CreateRiskReport({
                            customerId: customer.id,
                            customerName: customer.name,
                            redirectToScreening: payload.redirectToScreening
                        }));
                        _this.snackBar.success('All searches are valid, no screening refreshed, the new risk report is being generated');
                    }
                    else {
                        _this.snackBar.success('All searches are valid, no screening refreshed');
                    }
                }
                if (searchToBeDone.length > 0 && !payload.generateReport) {
                    if (payload.engineTypes
                        && payload.engineTypes.length === 1
                        && payload.engineTypes[0] === SearchEngineTypeValue.INTERNET_SEARCH
                        && isNlpEnabled) {
                        _this.snackBar.success("Internet Search screening for " + (curentCrp && !curentCrp.primary ? curentCrp.name + ' of' : '') + "\n                  customer " + customer.name + " has finished. Please wait for a while to see full NLP result.");
                    }
                    else if (payload.engineTypes
                        && payload.engineTypes.length === 1
                        && (payload.engineTypes[0] === SearchEngineTypeValue.OWN_NAME
                            || payload.engineTypes[0] === SearchEngineTypeValue.INTERNET_SEARCH)) {
                        _this.snackBar.success(_this.screeningService.getSearchEngineLabel(payload.engineTypes[0]) + "\n                Screening for " + (curentCrp && !curentCrp.primary ? curentCrp.name + ' of' : '') + "\n                customer " + customer.name + " has finished");
                    }
                    else {
                        _this.snackBar.success((payload.engineTypes ? _this.screeningService.getSearchEngineLabel(payload.engineTypes[0]) : '') + "\n             Screening for " + (curentCrp && !curentCrp.primary ? curentCrp.name + ' of ' : '') + "\n             customer " + customer.name + " is in progress, please wait or come back later.");
                    }
                }
                return zip(payload$, of(customer), of(crps), posts$.length === 0 ? of(null) : zip.apply(void 0, posts$), of(curentCrp));
            }), 
            // generate report if needed
            // must generate risk report here otherwise status won't update to pending
            // need to move whole logic to backend in the future
            switchMap(function (_a) {
                var payload = _a[0], customer = _a[1], crps = _a[2], post = _a[3], curentCrp = _a[4];
                return zip(of(null), of(payload), of(customer), of(crps), of(post), of(curentCrp));
            }), switchMap(function (_a) {
                var riskReport = _a[0], payload = _a[1], customer = _a[2], crps = _a[3], post = _a[4], curentCrp = _a[5];
                var getScreenings$ = [];
                if ((payload.crpId || curentCrp) && post && post.length > 0) {
                    post.filter(function (searchPost) { return !searchPost.error && searchPost; }).forEach(function (searchItem) {
                        getScreenings$.push(_this.screeningService.getScreening(searchItem.record, searchItem.engineType, {
                            limit: 999
                        }));
                    });
                }
                else {
                    getScreenings$ = [];
                }
                return zip(of(riskReport), of(payload), of(customer), of(crps), of(post), of(curentCrp), getScreenings$.length === 0 ? of(null) : zip.apply(void 0, getScreenings$));
            }), switchMap(function (_a) {
                var riskReport = _a[0], payload = _a[1], customer = _a[2], crps = _a[3], post = _a[4], curentCrp = _a[5], latestScreening = _a[6];
                var actions = [];
                // add or remove error data to state
                if ((payload.crpId || curentCrp) && latestScreening) {
                    var screening_1 = {};
                    if (latestScreening && latestScreening.length > 0) {
                        latestScreening.forEach(function (search) {
                            screening_1[search.engineType] = search;
                        });
                    }
                    _this.store.dispatch(new GetScreeningSuccess(screening_1));
                }
                if (post && post.length) {
                    var failedPosts = post.filter(function (searchPost) { return searchPost.error; });
                    var successedPosts = post.filter(function (searchPost) { return !searchPost.error; });
                    if (failedPosts.length > 0) {
                        failedPosts.map(function (search) {
                            if ((PUSHER_IGNORE_SEARCHES.indexOf(search.engineType) < 0)) {
                                actions.push(new SetScreeningError({
                                    recordId: search.record,
                                    searchEngineType: search.engineType
                                }));
                            }
                            else {
                                actions.push(new RemoveScreeningError({
                                    recordId: search.record,
                                    searchEngineType: search.engineType
                                }));
                            }
                        });
                        _this.snackBar.open(failedPosts, ScreeningErrorMessageComponent);
                    }
                    if (successedPosts.length > 0) {
                        successedPosts.forEach(function (search) {
                            if ((PUSHER_IGNORE_SEARCHES.indexOf(search.engineType) < 0 && search.isSearchFinished) ||
                                PUSHER_IGNORE_SEARCHES.indexOf(search.engineType) >= 0) {
                                actions.push(new RemoveScreeningError({
                                    recordId: search.record,
                                    searchEngineType: search.engineType
                                }));
                            }
                        });
                    }
                    if (failedPosts.length === 0) {
                        if (payload.generateReport &&
                            post.filter(function (searchItem) { return PUSHER_IGNORE_SEARCHES.indexOf(searchItem.engineType) < 0 && !searchItem.isSearchFinished; }).length > 0) {
                            _this.snackBar.success('Risk report will be generated soon. Please do not refresh the page and wait until the screening is done.');
                        }
                        else {
                            if (payload.generateReport) {
                                if (post.length > 0 &&
                                    post.map(function (p) { return p.engineType; }).every(function (engineType) { return PUSHER_IGNORE_SEARCHES.includes(engineType); })) {
                                    actions.push(new CreateRiskReport({
                                        customerId: customer.id,
                                        customerName: customer.name,
                                        redirectToScreening: payload.redirectToScreening
                                    }));
                                }
                                else {
                                    actions.push(new UpdateReport(riskReport));
                                }
                            }
                        }
                    }
                    var searchEnginePosts = post.map(function (i) { return i.engineType; });
                    // If all the searches is under ignored list, refresh the CRP to reload the conclusion
                    if (_this.arrayContainsArray(PUSHER_IGNORE_SEARCHES, searchEnginePosts) === true) {
                        if (payload.crpId || curentCrp) {
                            actions.push(new RootActions.customer.GetCrp({
                                crpId: payload.crpId || (curentCrp && curentCrp.id),
                                customerId: customer.id,
                                quietUpdate: true
                            }));
                        }
                    }
                }
                actions.push(new GetCustomerStatus({
                    customerId: customer.id
                }));
                actions.push(new PostScreeningSuccess());
                actions.push(new GetSearchValidity({
                    customerId: customer.id,
                    records: crps.map(function (crp) { return crp.record; })
                }));
                return actions;
            }), catchError(function (error) {
                _this.snackBar.error('Failed to screening this customer, please try again later.');
                return [new PostScreeningFailed()];
            }));
        }));
        this.GetScreeningFailed$ = this.actions$.pipe(ofType(SearchActionTypes.GET_SCREENING_FAILED), tap(function (error) {
            _this.snackBar.error(error.payload.error.message);
        }));
        this.GetSpecificScreening$ = this.actions$.pipe(ofType(SearchActionTypes.GET_SPECIFIC_SCREENING), map(function (res) { return res.payload; }), switchMap(function (input) {
            var requests$ = [];
            // Keep payload for next request
            var payload$ = of(input);
            requests$.push(payload$);
            return zip.apply(void 0, requests$).pipe(switchMap(function (_a) {
                var payload = _a[0];
                return _this.screeningService.getScreening(payload.recordId, payload.engineType, payload.parrams);
            }), withLatestFrom(_this.store.select(Selectors.search.screening)), switchMap(function (_a) {
                var res = _a[0], screening = _a[1];
                screening[res.engineType] = res;
                return [
                    new GetSpecificScreeningSuccess(tslib_1.__assign({}, screening)),
                    new RemoveScreeningError({
                        recordId: input.recordId,
                        searchEngineType: input.engineType
                    })
                ];
            }), catchError(function (error) {
                return [new GetSpecificScreeningFailed(input.engineType),
                    new SetScreeningError({
                        recordId: input.recordId,
                        searchEngineType: input.engineType
                    })];
            }));
        }));
        this.GetSpecificScreeningFailed$ = this.actions$.pipe(ofType(SearchActionTypes.GET_SPECIFIC_SCREENING_FAILED), tap(function (error) {
            _this.snackBar.error("Error: Failed to screen with " + error.payload + " ");
        }));
        this.PatchSearchResult$ = this.actions$.pipe(ofType(SearchActionTypes.PATCH_SEARCH_RESULT), map(function (res) { return res.payload; }), switchMap(function (payload) {
            return _this.screeningService.batchUpdateSearchResult(payload).pipe(switchMap(function (res) {
                _this.snackBar.success('Screening result updated successfully.');
                return of(new PatchSearchResultSuccess(res));
            }), catchError(function (error) {
                _this.snackBar.error('Error: Failed to update screening result, please try again later.');
                return of(new PatchSearchResultFailed({ error: error }));
            }));
        }));
        this.PatchSearchResultFailed$ = this.actions$.pipe(ofType(SearchActionTypes.PATCH_SEARCH_RESULT_FAILED), tap(function (error) {
            _this.snackBar.error(error.payload.error.message);
        }));
        this.GetSearchValidity$ = this.actions$.pipe(ofType(SearchActionTypes.GET_SEARCH_VALIDITY), map(function (input) { return input.payload; }), switchMap(function (input) {
            return _this.store.select(Selectors.search.searchValidity).pipe(take(1), switchMap(function (res) {
                return _this.store
                    .select(Selectors.billable.getSearchEngines)
                    .pipe(filter(function (searchEngines) { return !!searchEngines && !!searchEngines.length; }), take(1), map(function (searchEngines) { return searchEngines.filter(function (searchEngine) { return searchEngine.display; }); }), concatMap(function (engines) {
                    if (res) {
                        if (res.customerId === input.customerId) {
                            return of(res.searchValidity);
                        }
                        else {
                            return _this.screeningService.checkValidSearch(input.records, engines);
                        }
                    }
                    else {
                        return _this.screeningService.checkValidSearch(input.records, engines);
                    }
                }));
            }), switchMap(function (validity) {
                return of(new GetSearchValiditySuccess({
                    customerId: input.customerId,
                    searchValidity: validity
                }));
            }), catchError(function (err) { return of(new GetSearchValidityFailure(err)); }));
        }));
    }
    SearchEffects.prototype.arrayContainsArray = function (superset, subset) {
        return subset.every(function (value) {
            return (superset.indexOf(value) >= 0);
        });
    };
    tslib_1.__decorate([
        Effect(),
        tslib_1.__metadata("design:type", Observable)
    ], SearchEffects.prototype, "GetScreening$", void 0);
    tslib_1.__decorate([
        Effect(),
        tslib_1.__metadata("design:type", Observable)
    ], SearchEffects.prototype, "PostScreening$", void 0);
    tslib_1.__decorate([
        Effect({ dispatch: false }),
        tslib_1.__metadata("design:type", Observable)
    ], SearchEffects.prototype, "GetScreeningFailed$", void 0);
    tslib_1.__decorate([
        Effect(),
        tslib_1.__metadata("design:type", Observable)
    ], SearchEffects.prototype, "GetSpecificScreening$", void 0);
    tslib_1.__decorate([
        Effect({ dispatch: false }),
        tslib_1.__metadata("design:type", Observable)
    ], SearchEffects.prototype, "GetSpecificScreeningFailed$", void 0);
    tslib_1.__decorate([
        Effect(),
        tslib_1.__metadata("design:type", Observable)
    ], SearchEffects.prototype, "PatchSearchResult$", void 0);
    tslib_1.__decorate([
        Effect({ dispatch: false }),
        tslib_1.__metadata("design:type", Observable)
    ], SearchEffects.prototype, "PatchSearchResultFailed$", void 0);
    tslib_1.__decorate([
        Effect(),
        tslib_1.__metadata("design:type", Observable)
    ], SearchEffects.prototype, "GetSearchValidity$", void 0);
    return SearchEffects;
}());
export { SearchEffects };
